import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import StoreCreationTeamSelectStep from './StoreCreationTeamSelectStep';
import StoreCreationStoreTypeStep from './StoreCreationStoreTypeStep';
import StoreCreationSelectProductsStep from './StoreCreationSelectProductsStep';
import StoreCreationSetupCustomProductsStep from './StoreCreationSetupCustomProductsStep';
import StoreCreationStoreSettingsStep from './StoreCreationSettingsStep';
import StoreCreationNameStep from './StoreCreationNameStep';
import CreationWizard from './CreationWizard';
import StoreCreationAdminStockContentStep from './StoreCreationAdminStockContentStep';
import StoreCreationStockContentStep from './StoreCreationStockContentStep';
import StoreCreationAssignAdminsStep from './StoreCreationAssignAdminsStep';
import StoreCreationWebstoreStep from './StoreCreationWebstoreStep';

const STORE_CREATION_SETUP_STEPS = [
  {
    title: 'Is this a team store?',
    description: 'Select whether this store is for personal use or for a team',
    value: 'store-type',
    Component: StoreCreationStoreTypeStep
  },
  {
    title: 'Select your team',
    description:
      'Select a team to manage this store and prefill settings accordingly',
    Component: StoreCreationTeamSelectStep,
    value: 'select-team',
    isVisible: (formState) => formState.storeType === 'team'
  },
  {
    title: 'What would you like to sell?',
    description: 'Prefill your store with stock content ready to go',
    value: 'admin-stock-content',
    Component: StoreCreationAdminStockContentStep,
    isVisible: (formState, isSportsHeadzAdmin) => isSportsHeadzAdmin
  },
  {
    title: 'What would you like to sell?',
    description: 'Prefill your store with stock content ready to go',
    value: 'stock-content',
    Component: StoreCreationStockContentStep,
    isVisible: (formState, isSportsHeadzAdmin) =>
      !isSportsHeadzAdmin ||
      (formState.copyContent.ecomStore && !formState.copyContent.copyProducts)
  },
  // {
  //   title: 'Design custom products?',
  //   description: 'Choose to add custom design products to your store',
  //   value: 'custom-products',
  //   Component: StoreCreationSetupCustomProductsStep,
  //   isVisible: (formState) =>
  //     !formState.copyContent.ecomStore || !formState.copyContent.copyProducts
  // },
  {
    title: 'Select products to sell',
    description: 'Choose products to sell and set your markup',
    value: 'products-and-markup',
    Component: StoreCreationSelectProductsStep,
    isVisible: (formState) =>
      (!formState.copyContent.ecomStore ||
        !formState.copyContent.copyProducts) &&
      (formState.customProducts.includeCustomProducts ||
        formState.ecomStockContent.length > 0)
  },
  {
    title: 'Store settings',
    description: 'Configure your store',
    value: 'store-settings',
    Component: StoreCreationStoreSettingsStep
  },
  {
    title: 'Webstore',
    description: 'Set up your webstore appearance',
    value: 'webstore',
    Component: StoreCreationWebstoreStep
  },
  {
    title: 'Assign admins',
    description: 'Assign administrators to manage your store',
    value: 'assign-admins',
    Component: StoreCreationAssignAdminsStep,
    isVisible: (formState, isSportsHeadzAdmin) =>
      isSportsHeadzAdmin &&
      (!formState.copyContent.ecomStore || !formState.copyContent.copyAdmins)
  },
  {
    title: 'Name your store',
    description: 'Choose a name for your store',
    value: 'name-store',
    Component: StoreCreationNameStep
  }
];

const initialStoreFormState = {
  loading: false,
  storeType: '',
  team: null,
  admins: [],
  copyThemeFromStockContent: true,
  selectedProductsOption: true,
  pkEcomStoreTheme: null,
  pkEcomVendorProducts: [],
  ecomStockContent: [],
  storeSettings: {
    name: '',
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    logoImageUrl: '',
    supportEmail: '',
    fundraisingWindowSize: null
  },
  customProducts: {
    includeCustomProducts: false,
    pkEcomExternalProducts: [],
    imageUrl: null,
    imageFile: null,
    imagePreview: null
  },
  copyContent: {
    ecomStore: null,
    copyProducts: true,
    copyWebstore: true,
    copyFundraisers: true,
    copyBranding: true,
    copyTaxes: true,
    copyMedia: true,
    copyAdmins: true
  }
};

const StoreCreationWizardView = ({}) => {
  const navigate = useNavigate();
  const {
    state: authState,
    fetchEcommerceAdminData,
    selectEcomStore
  } = useContext(AuthContext);
  const { state, fetchStoreCreateOptions, createStore } = useContext(
    StoreContext
  );
  const [formState, setFormState] = useState({ ...initialStoreFormState });

  const { loading, value: createOptions } = state.ecomStoreCreateOptions;

  useEffect(() => {
    if (!createOptions && !loading) {
      fetchStoreCreateOptions(null, triggerNotification);
    }

    setFormState({
      ...initialStoreFormState,
      admins: [authState.userData.user]
    });
  }, []);

  const onCreateStore = (s, { hasSkipped }) => {
    setFormState((prevState) => ({ ...prevState, loading: true }));

    const useNewTheme =
      (!formState.copyThemeFromStockContent ||
        formState.ecomStockContent.length === 0) &&
      (!formState.copyContent.ecomStore || !formState.copyContent.copyWebstore);

    createStore(
      hasSkipped
        ? { ecomStore: { name: formState.storeSettings.name } }
        : {
            webstoreLayout: useNewTheme
              ? formState.storeSettings.webstoreLayout
              : null,
            ecomStore: {
              name: formState.storeSettings.name,
              logoImageUrl: formState.storeSettings.logoImageUrl,
              supportEmail: formState.storeSettings.supportEmail,
              primaryColor: formState.storeSettings.primaryColor,
              secondaryColor: formState.storeSettings.secondaryColor,
              bannerImageUrl: formState.storeSettings.bannerImageUrl,
              ecomStoreWindow: formState.storeSettings.fundraisingWindowSize
                ? {
                    name: 'Fundraising Window',
                    CloseDate: new Date(
                      new Date().setMonth(
                        new Date().getMonth() +
                          (formState.storeSettings.fundraisingWindowSize ===
                          '1_month'
                            ? 1
                            : formState.storeSettings.fundraisingWindowSize ===
                              '3_months'
                            ? 3
                            : formState.storeSettings.fundraisingWindowSize ===
                              '6_months'
                            ? 6
                            : 12)
                      )
                    ).toISOString()
                  }
                : null,
              urlHandle: formState.storeSettings.urlHandle,
              fkTeam: formState.team?.pkTeam || null,
              fkAssociation: formState.team?.fkAssociation || null
            },
            pkUserAdmins: formState.admins.map((a) => a.pkUser),
            ecomStockContent: formState.ecomStockContent.map((c) => ({
              ...c,
              pkEcomVendorProducts:
                formState.selectedProductsOption === 'all'
                  ? null
                  : formState.pkEcomVendorProducts
            })),
            ecomStoreCopy:
              authState.userData.isAdmin && formState.copyContent.ecomStore
                ? {
                    ...formState.copyContent,
                    pkEcomStore: formState.copyContent.ecomStore.pkEcomStore
                  }
                : null
          },
      (data) => {
        fetchEcommerceAdminData(
          () => {
            selectEcomStore(data.pkEcomStore);
            triggerNotification('Store created!', 'success');
            navigate('/merchant/');
          },
          () => {
            window.location.reload();
          }
        );
      },
      (e) => {
        setFormState((prevState) => ({ ...prevState, loading: false }));
        triggerNotification(e);
      }
    );
  };

  return (
    <CreationWizard
      createOptions={createOptions}
      formState={formState}
      isSportsHeadzAdmin={authState.userData.isAdmin}
      loading={loading}
      onCreate={onCreateStore}
      setFormState={setFormState}
      steps={STORE_CREATION_SETUP_STEPS}
      submitting={formState.loading}
    />
  );
};

StoreCreationWizardView.propTypes = {};

export default StoreCreationWizardView;
